<script setup>

import {onMounted, ref} from "vue";
import ButtonMoreOptionsOptionWithConfirmation
    from "../../components/buttons/buttonMoreOptionsOptionWithConfirmation.vue";
import ButtonMoreOptions from "../../components/buttons/buttonMoreOptions.vue";

const taskLists = ref([])
const templates = ref([])
const templateId = ref(null)
const templateName = ref('')
const templateTasks = ref([])
const sharingTemplate = ref(null)

const loadTaskLists = async () => {
    const response = await axios.get(route('api.task-list.index'))
    taskLists.value = response.data.task_lists
}

const loadTaskListTemplates = async () => {
    const response = await axios.get(route('api.task-list-template.index'))
    templates.value = response.data.task_list_templates
}

const createTemplateFromList = async (list) => {
    const response = await axios.post(route('api.task-list-template.create-from-task-list'), {
        task_list_id: list.id,
    })

    templateName.value = response.data.name
    templateTasks.value = response.data.tasks
}

const shareTaskListTemplate = (template) => {
    sharingTemplate.value = template
}

const editTaskListTemplate = (template) => {
    templateId.value = template.id
    templateName.value = template.name
    templateTasks.value = template.tasks
    window.scrollTo(0, 0, {
        behavior: 'smooth',
    })
}

const deleteTaskList = async (taskListId) => {
    await axios.delete(route('api.task-list.delete', taskListId))
    onReload()
}

const onReload = () => {
    loadTaskLists()
    loadTaskListTemplates()
    onCancel()
    window.location.hash = 'templates'
}

const onCancel = () => {
    templateId.value = null
    templateName.value = ''
    templateTasks.value = []
    window.scrollTo(0, 0, {
        behavior: 'smooth',
    })
}

const onClose = () => {
    sharingTemplate.value = null
}


onMounted(() => {
    loadTaskLists()
    loadTaskListTemplates()
})

</script>
<template>
    <div>
        <div v-if="templateTasks.length > 0">
            <task-template-task-list
                :list-id="templateId"
                :list-name="templateName"
                :template-tasks="templateTasks"
                @cancel="onCancel"
                @reload="onReload"
            ></task-template-task-list>
        </div>

        <div
            v-if="templateTasks.length === 0"
            class="mt-16"
        >
            <card-tabbed
                :track-state="true"
            >
                <card-tabbed-tab
                    value="templates"
                    title="Templates"
                >
                    <ul>
                        <li
                            v-if="templates.length === 0"
                            class="flex justify-between items-center py-4 text-gray-400 text-center"
                        >
                            You have no task list templates yet
                        </li>
                        <li
                            v-for="(template, i) in templates"
                            class="flex justify-between items-center py-1 hover:bg-gray-50"
                        >
                            <div class="space-x-2">
                                <span>{{ template.name }}</span> <span class="text-gray-400">{{ template.tasks.length }} tasks</span>
                            </div>
                            <div class="flex space-x-2">
                                <button-secondary
                                    size="sm"
                                    @click="shareTaskListTemplate(template)"
                                >Share</button-secondary>
                                <button-base
                                    size="sm"
                                    @click="editTaskListTemplate(template)"
                                >Edit</button-base>
                            </div>
                        </li>
                    </ul>
                    <p
                        v-if="templates.length > 0"
                        class="mt-4 text-xs text-gray-400"
                    >
                        Share a task list to any of your users, or with yourself to make adjustments and create a new template.<br>
                        When you share a task list, it will show up as an invite among the user's task lists.
                    </p>
                </card-tabbed-tab>
                <card-tabbed-tab
                    value="tasklists"
                    title="Task lists"
                    :active="true"
                >
                    <ul class="divide-y divide-gray-200">
                        <li
                            v-if="taskLists.length === 0"
                            class="flex justify-between items-center py-4 text-gray-400 text-center py-2 px-4 md:px-6 lg:px-8 -mx-4 md:-mx-6 lg:-mx-8"
                        >
                            You have no task list yet, create one to make a template out of it
                        </li>
                        <li
                            v-for="(list, i) in taskLists"
                            :key="list.id"
                            class="flex justify-between items-start space-x-2 hover:bg-gray-50 py-2 px-4 md:px-6 lg:px-8 -mx-4 md:-mx-6 lg:-mx-8"
                        >
                            <div class="pt-0.5">
                                {{ list.name }}
                                <span class="text-gray-400 text-xs ml-1">({{ list.tasks.length }})</span>
                            </div>

                            <div class="flex justify-between space-x-2">
                                <button-more-options
                                    alignment="left"
                                    size="sm"
                                    :cancel-on-outside-click="false"
                                >
                                    <button-more-options-option-with-confirmation
                                        style-type="danger"
                                        confirm-text="Are you sure you want to delete this task list?"
                                        confirm-button-label="Delete"
                                        abort-button-label="Cancel"
                                        @confirmed="deleteTaskList(list.id)"
                                        class="w-36"
                                    >
                                        Delete task list
                                    </button-more-options-option-with-confirmation>
                                </button-more-options>
                                <button-secondary
                                    v-if="list.tasks.length > 0"
                                    size="sm"
                                    class="min-w-28 justify-center"
                                    @click="createTemplateFromList(list)"
                                >Create template</button-secondary>
                                <button-secondary
                                    v-if="list.tasks.length === 0"
                                    disabled
                                    size="sm"
                                    class="min-w-28 justify-center"
                                >No tasks</button-secondary>
                            </div>
                        </li>
                    </ul>
                    <p
                        v-if="taskLists.length > 0"
                        class="mt-4 text-xs text-gray-400"
                    >
                        These are your task lists. Create a template from a task list, and share it with any of your users.
                    </p>
                </card-tabbed-tab>
            </card-tabbed>
        </div>

        <task-template-share-slideout
            :sharing-template="sharingTemplate"
            @close="onClose"
        />
    </div>
</template>
